import { useEffect, useState } from 'react';
import { Table, Button, DatePicker, InputNumber, Form, message, Popconfirm, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../api';

const { Text } = Typography;

export default function CommissionModelsTab({ storeId }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setModels(await api.commissionModels.list(storeId));
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on storeId change, not on load/form identity
  useEffect(() => { load(); form.resetFields(); }, [storeId]);

  const submit = async (values) => {
    setSaving(true);
    try {
      await api.commissionModels.create({
        store_id: storeId,
        valid_from: values.valid_from.format('YYYY-MM-DD'),
        delivery_percent: values.delivery_percent || 0,
        delivery_fixed: values.delivery_fixed || 0,
        pickup_percent: values.pickup_percent || 0,
        pickup_fixed: values.pickup_fixed || 0,
      });
      message.success('Provisionsmodell gespeichert');
      form.resetFields();
      load();
    } catch (err) {
      message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.commissionModels.delete(id);
      load();
    } catch (err) {
      message.error(err.message);
    }
  };

  const columns = [
    { title: 'Gültig ab', dataIndex: 'valid_from', key: 'valid_from', render: v => dayjs(v).format('DD.MM.YYYY') },
    { title: 'Lieferung %', dataIndex: 'delivery_percent', key: 'delivery_percent', align: 'right' },
    { title: 'Lieferung Fix (€)', dataIndex: 'delivery_fixed', key: 'delivery_fixed', align: 'right' },
    { title: 'Abholung %', dataIndex: 'pickup_percent', key: 'pickup_percent', align: 'right' },
    { title: 'Abholung Fix (€)', dataIndex: 'pickup_fixed', key: 'pickup_fixed', align: 'right' },
    {
      title: '', key: 'actions', width: 60,
      render: (_, r) => (
        <Popconfirm title="Modell löschen?" onConfirm={() => remove(r.id)} okText="Löschen" cancelText="Abbrechen">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Für eine Bestellung gilt jeweils das Modell mit dem größten „Gültig ab"-Datum, das nicht
        nach dem Bestelldatum liegt. Bereits erzeugte Rechnungen bleiben unverändert, auch wenn
        sich das Modell später ändert.
      </Text>

      <Form form={form} layout="inline" onFinish={submit} style={{ marginBottom: 16, flexWrap: 'wrap', rowGap: 8 }}>
        <Form.Item name="valid_from" label="Gültig ab" rules={[{ required: true }]}>
          <DatePicker format="DD.MM.YYYY" />
        </Form.Item>
        <Form.Item name="delivery_percent" label="Lieferung %">
          <InputNumber min={0} max={100} step={0.5} />
        </Form.Item>
        <Form.Item name="delivery_fixed" label="Lieferung Fix (€)">
          <InputNumber min={0} step={0.1} />
        </Form.Item>
        <Form.Item name="pickup_percent" label="Abholung %">
          <InputNumber min={0} max={100} step={0.5} />
        </Form.Item>
        <Form.Item name="pickup_fixed" label="Abholung Fix (€)">
          <InputNumber min={0} step={0.1} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={saving}>
            Hinzufügen
          </Button>
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={models}
        loading={loading}
        pagination={false}
        size="middle"
      />
    </>
  );
}
